import './ManageCallModal.scss'
import { Modal } from '../../components/Modal/Modal'
import { Col, Container, Row, Spinner, Stack, Toast, ToastContainer } from 'react-bootstrap'
import React, { FunctionComponent, PropsWithChildren, SVGProps, useCallback, useEffect, useMemo, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { formatPhoneNumber } from 'react-phone-number-input'
import { Button } from '../../components/Button/Button'
import { CallSolution, IntegrationServiceId, supportedCallSolutions } from '../../common/integrations'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetUserTemporaryCodeMutation } from '../../store/userTemporaryCode/userTemporaryCode'
import { formatDate } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { UserTemporaryCode } from '../../models/UserTemporaryCode'
import { MeetingProviderAccountIntegrationConfig } from '../../models/Account'
import { RoomSession } from '../../models/Room'
import { useGetAllServiceLocationsQuery } from '../../store/user/user'
import { parseMetadata } from '../../common/util'
import { useNavigate } from 'react-router-dom'

interface CallDetailsButtonProps {
  assistantPhoneNumber: string
  temporaryCode: UserTemporaryCode
  ProviderIcon?: FunctionComponent<SVGProps<SVGSVGElement> & { title?: string | undefined }>
}

interface CallDetailsBlockProps {
  assistantPhoneNumber: string
  temporaryCode: UserTemporaryCode
  provider?: CallSolution
  CallDetailsButton: React.FC<CallDetailsButtonProps>
  refreshUserTemporaryCode: () => void
}

const CallDetailsBlock: React.FC<CallDetailsBlockProps> = ({ assistantPhoneNumber, temporaryCode, provider, CallDetailsButton, refreshUserTemporaryCode }) => {
  const [showToast, setShowToast] = useState(false)
  const expiresAt = temporaryCode.expiresAt
  const [expired, setExpired] = useState(!!expiresAt && new Date(expiresAt).getTime() <= Date.now())
  useEffect(() => {
    if (expiresAt) {
      const timeout = setTimeout(() => setExpired(true), new Date(expiresAt).getTime() - Date.now())
      return () => clearTimeout(timeout)
    }
  }, [expiresAt])

  const handleCopy = async () => {
    await navigator.clipboard.writeText(`${assistantPhoneNumber},,${temporaryCode.code}`)
    setShowToast(true)
  }

  return (
    <Container className='d-flex flex-column justify-content-center paragraph-small zocks-text-gray-900 gap-4'>
      <ToastContainer position='top-center' className='p-3'>
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={1500} autohide>
          <Toast.Body>Copied to clipboard!</Toast.Body>
        </Toast>
      </ToastContainer>
      <Row className='row-cols-sm-auto justify-content-center align-self-stretch'>
        <Col>
          <Stack direction='vertical' gap={2} className='paragraph-medium'>
            <div>Call your assistant</div>
            <div>Unique extension</div>
            <div>Valid until</div>
          </Stack>
        </Col>
        <Col>
          <Stack direction='vertical' gap={2} className='paragraph-medium bold'>
            <div>{formatPhoneNumber(assistantPhoneNumber)}</div>
            <div>#{temporaryCode.code}</div>
            {expired ? <div className='zocks-text-danger'>Expired</div> : <div>{expiresAt ? formatDate(expiresAt) : '-'}</div>}
          </Stack>
        </Col>
      </Row>
      <Row>
        <Col className='d-flex align-items-center justify-content-center'>
          {expired ? (
            <Button variant='primary' className='w-100' onClick={refreshUserTemporaryCode}>
              Refresh extension
            </Button>
          ) : (
            <Stack direction='vertical' gap={2}>
              <CallDetailsButton assistantPhoneNumber={assistantPhoneNumber} temporaryCode={temporaryCode} ProviderIcon={provider?.icon} />
              <Button variant='link' className='text-decoration-none zocks-text-primary button-small w-100' onClick={handleCopy}>
                Copy phone number
              </Button>
            </Stack>
          )}
        </Col>
      </Row>
    </Container>
  )
}

export type CallNumberBodyProps = {
  title?: string
  assistantPhoneNumber: string
  temporaryCode: UserTemporaryCode
  refreshUserTemporaryCode: () => void
}
export const CallNumberBody: React.FC<CallNumberBodyProps> = ({ assistantPhoneNumber, temporaryCode, refreshUserTemporaryCode }) => {
  const createPhoneData = () => {
    return `tel:${assistantPhoneNumber},,${temporaryCode.code}`
  }

  const NoProviderCallDetailsButton: React.FC<CallDetailsButtonProps> = ({ assistantPhoneNumber, temporaryCode }) => (
    <div className='d-flex align-items-center justify-content-center px-3 py-2'>
      <Button variant='primary' className='w-100'>
        <i className='bi bi-telephone' />
        <a className='text-decoration-none zocks-text-white' href={createPhoneData()} target='_blank' rel='noopener noreferrer'>
          {`${formatPhoneNumber(assistantPhoneNumber)}#${temporaryCode.code}`}
        </a>
      </Button>
    </div>
  )

  return (
    <Container fluid className='d-flex flex-column px-0 gap-4'>
      <Row className='mx-0'>
        <Col className='px-0'>
          <div>
            Scan the QR code or use the phone number below to add AI Assistant to your call. Create a Group or Conference call and invite the assistant to join.
          </div>
        </Col>
      </Row>
      <Row className='mx-0'>
        <Col className='d-flex px-0 justify-content-center'>
          <QRCodeSVG value={createPhoneData()} size={200} marginSize={2} title='Call Zocks Assistant' />
        </Col>
      </Row>
      <Row className='mx-0'>
        <Col className='px-0'>
          <CallDetailsBlock
            assistantPhoneNumber={assistantPhoneNumber}
            temporaryCode={temporaryCode}
            CallDetailsButton={NoProviderCallDetailsButton}
            refreshUserTemporaryCode={refreshUserTemporaryCode}
          />
        </Col>
      </Row>
    </Container>
  )
}

export type AddAssistantBodyProps = {
  title?: string
  assistantPhoneNumber: string
  temporaryCode: UserTemporaryCode
  provider: CallSolution
  doCall: boolean
  refreshUserTemporaryCode: () => void
}
export const AddAssistantBody: React.FC<AddAssistantBodyProps> = ({ assistantPhoneNumber, temporaryCode, provider, doCall, refreshUserTemporaryCode }) => {
  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noreferrer')
  }

  const desktopIntegrationUrl = useMemo(() => {
    if (provider && assistantPhoneNumber && temporaryCode) {
      return provider.getLink(assistantPhoneNumber, temporaryCode.code)
    }
    return null
  }, [provider, assistantPhoneNumber, temporaryCode])

  useEffect(() => {
    if (doCall) {
      if (desktopIntegrationUrl) {
        openInNewTab(desktopIntegrationUrl)
      }
    }
  }, [doCall, desktopIntegrationUrl])

  interface SetupBlockProps {
    provider: CallSolution
    doCall: boolean
  }

  const DefaultSetupBlock: React.FC<SetupBlockProps> = ({ provider, doCall }) => {
    return (
      <>
        {doCall ? (
          <>
            <div className='paragraph-medium zocks-text-gray-900'>
              {provider.displayName} is opening automatically. If {provider.displayName} doesn't open, dial this number directly in your {provider.displayName}{' '}
              app.
            </div>
          </>
        ) : (
          <>
            <div className='paragraph-medium zocks-text-gray-900'>
              Add your Zocks AI Assistant to an ongoing call, or create a new Group or Conference call in {provider.displayName} and invite the assistant using
              the number below.
            </div>
          </>
        )}
      </>
    )
  }

  const IntulseSetupBlock: React.FC<SetupBlockProps> = () => {
    const IntulseLink: React.FC<PropsWithChildren> = ({ children }) => (
      <a href='https://support.intulse.com/app/walkthrough/access-intulse-tools/intulse-companion-app' target='_blank' rel='noopener noreferrer'>
        {children}
      </a>
    )

    return (
      <>
        <h5 className='pb-3'>Start a call in Intulse</h5>
        <ol>
          <li>
            Ensure the <IntulseLink>Intulse Browser Extension</IntulseLink> is installed.
          </li>
          <li>Click the phone number below.</li>
          <li>Choose 'Start a Call'.</li>
          <li>Enter your extension code when connected.</li>
        </ol>
        <div className='paragraph-medium zocks-text-gray-900'>
          For ongoing calls, start a conference call and dial the assistant number with your unique code.
        </div>
      </>
    )
  }

  const DefaultCallDetailsButton: React.FC<CallDetailsButtonProps> = ({ assistantPhoneNumber, temporaryCode, ProviderIcon }) => {
    return (
      <Stack
        direction='horizontal'
        gap={2}
        className='border zocks-border-color-gray-300 rounded-1 button-large align-items-center justify-content-center zocks-bg-color-gray-100 px-3 py-2 w-100'
      >
        {ProviderIcon && <ProviderIcon width={'2rem'} height={'2rem'} />}
        <div className='text-decoration-none'>{`${formatPhoneNumber(assistantPhoneNumber)}#${temporaryCode.code}`}</div>
      </Stack>
    )
  }

  const IntulseCallDetailsButton: React.FC<CallDetailsButtonProps> = ({ assistantPhoneNumber, temporaryCode, ProviderIcon }) => {
    return (
      <div className='call-details-button button-large d-flex py-2 px-3 justify-content-center align-items-center zocks-gap-0.75 rounded-1'>
        {ProviderIcon && <ProviderIcon width={'2rem'} height={'2rem'} />}
        <div className='text-decoration-none'>{`${formatPhoneNumber(assistantPhoneNumber)} #${temporaryCode.code}`}</div>
      </div>
    )
  }

  const CallDetailsButton = provider.id === 'intulse' ? IntulseCallDetailsButton : DefaultCallDetailsButton

  const SetupBlock = provider.id === 'intulse' ? IntulseSetupBlock : DefaultSetupBlock

  return (
    <Container fluid className='d-flex flex-column px-0 gap-4'>
      <Row className='mx-0'>
        <Col className='px-0'>
          <SetupBlock provider={provider} doCall={doCall} />
        </Col>
      </Row>
      <Row className='mx-0'>
        <Col className='px-0'>
          <CallDetailsBlock
            assistantPhoneNumber={assistantPhoneNumber}
            temporaryCode={temporaryCode}
            provider={provider}
            CallDetailsButton={CallDetailsButton}
            refreshUserTemporaryCode={refreshUserTemporaryCode}
          />
        </Col>
      </Row>
    </Container>
  )
}

export type ManageCallModalProps = {
  session: RoomSession | null
  show: boolean
  phoneConfig: MeetingProviderAccountIntegrationConfig
  doCall: boolean
  showOpenMeeting?: boolean
  onHide: () => void
}

export const ManageCallModal: React.FC<ManageCallModalProps> = ({ session, show, phoneConfig, doCall, showOpenMeeting = true, onHide }) => {
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const { external: { title = null } = {} } = useMemo(() => parseMetadata(session?.metadata ?? '{}'), [session])
  const {
    data: locations,
    isError: isLocationError,
    isFetching: locationsFetching,
  } = useGetAllServiceLocationsQuery({
    accountId: accountId,
    serviceId: IntegrationServiceId.PHONE,
  })
  const [getUserTemporaryCode, { data: userTemporaryCode, isError: isUserTemporaryCodeError, isLoading: userTemporaryCodeFetching }] =
    useGetUserTemporaryCodeMutation()

  const phoneNumber = useMemo(
    () =>
      locations?.data?.find((location) => location.id === phoneConfig.defaultLocation)?.locationDetails?.phoneNumber ??
      (locations?.data.length ? locations.data[0]?.locationDetails?.phoneNumber : null),
    [locations, phoneConfig],
  )

  const provider = useMemo(() => {
    return supportedCallSolutions.find((integration) => integration.id === phoneConfig.defaultProvider) ?? null
  }, [phoneConfig])

  const refreshUserTemporaryCode = useCallback(() => {
    getUserTemporaryCode({ type: 'phone', accountId: accountId, ...(session && { body: { type: 'session', roomId: session.roomId, sessionId: session.id } }) })
  }, [accountId, getUserTemporaryCode, session])

  useEffect(() => {
    refreshUserTemporaryCode()
  }, [accountId, refreshUserTemporaryCode])

  const handleOnHide = () => {
    onHide()
  }

  const handleOpenMeeting = () => {
    if (session) {
      navigate(`/workspace/live-meetings/room/${session.roomId}/session/${session.id}`)
    }
  }

  const getBody = () => {
    if (locationsFetching || userTemporaryCodeFetching) {
      return (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      )
    }
    if (isLocationError || !phoneNumber) {
      return <div>Failed to get assistant phone number, please try again!</div>
    }
    if (isUserTemporaryCodeError || !userTemporaryCode) {
      return <div>Failed to get code, please try again!</div>
    }
    return phoneConfig.defaultProvider && provider ? (
      <AddAssistantBody
        title={title}
        assistantPhoneNumber={phoneNumber}
        temporaryCode={userTemporaryCode.data}
        provider={provider}
        doCall={doCall}
        refreshUserTemporaryCode={refreshUserTemporaryCode}
      />
    ) : (
      <CallNumberBody
        title={title}
        assistantPhoneNumber={phoneNumber}
        temporaryCode={userTemporaryCode.data}
        refreshUserTemporaryCode={refreshUserTemporaryCode}
      />
    )
  }

  const footer = (
    <Container fluid className='p-0'>
      <Row className='d-flex align-items-center m-0'>
        <Col className='p-0'>
          <a
            href='https://help.zocks.io/en/articles/10226330-how-do-i-use-zocks-with-a-shared-line-or-a-software-based-phone-solution'
            target='_blank'
            rel='noreferrer'
          >
            <Stack direction='horizontal' gap={2}>
              <i className='bi bi-info-square zocks-text-gray-900' />
              <div className='paragraph-medium zocks-text-gray-900'>Help Center</div>
            </Stack>
          </a>
        </Col>
        <Col className='d-flex flex-row p-0 gap-3'>
          <Button variant={'secondary'} className='label-medium bold ms-auto' onClick={handleOnHide}>
            Close
          </Button>
          {!!session && showOpenMeeting && (
            <Button variant={'primary'} className='label-medium bold ms-auto text-nowrap' onClick={handleOpenMeeting}>
              Open meeting
            </Button>
          )}
        </Col>
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={handleOnHide}
      header={<h4>Add AI Assistant to your call</h4>}
      body={getBody()}
      footer={footer}
      centered={true}
      className='manage-call-modal'
      autoFocus={false}
    />
  )
}
